/**
 * Stock Alert Service
 * Detecta productos con stock bajo y envía notificaciones in-app + escritorio.
 * - Notifica al iniciar sesión (una vez por sesión del navegador)
 * - Notifica inmediatamente al registrar una venta que baja el stock
 * - No repite la misma alerta dentro de la misma sesión a menos que el stock baje más
 */

import { sendDesktopNotification } from './desktopNotifications';
const SESSION_KEY = 'savidna-stock-alerted'; // sessionStorage — se limpia al cerrar pestaña

interface AlertRecord {
  stockAtAlert: number;
  sentAt: number;
}
function loadRecords(): Record<string, AlertRecord> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveRecords(records: Record<string, AlertRecord>) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(records));
  } catch {}
}
function shouldAlert(productId: string, currentStock: number, records: Record<string, AlertRecord>): boolean {
  const rec = records[productId];
  if (!rec) return true; // Never alerted
  if (currentStock < rec.stockAtAlert) return true; // Stock dropped further
  return false; // Already alerted at this level
}
export interface LowStockProduct {
  id: string;
  name: string;
  stock: number;
  minStock: number;
  sku?: string;
}

/**
 * Verifica productos con stock bajo y dispara notificaciones.
 */
export function checkLowStockAlerts(products: LowStockProduct[], addNotification: (n: any) => void) {
  const records = loadRecords();
  const lowItems = products.filter(p => p.stock <= (p.minStock ?? 5) && p.stock >= 0);
  const toAlert = lowItems.filter(p => shouldAlert(p.id, p.stock, records));
  if (toAlert.length === 0) return;

  // Save records
  toAlert.forEach(p => {
    records[p.id] = {
      stockAtAlert: p.stock,
      sentAt: Date.now()
    };
  });
  saveRecords(records);
  const outOfStock = toAlert.filter(p => p.stock === 0);
  const lowOnly = toAlert.filter(p => p.stock > 0);
  if (toAlert.length === 1) {
    const p = toAlert[0];
    const isOut = p.stock === 0;
    addNotification({
      type: 'low_stock',
      title: isOut ? ' Producto Agotado' : ' Stock Bajo',
      message: isOut ? `"${p.name}" está agotado (0 unidades). Reabastece pronto.` : `"${p.name}" tiene solo ${p.stock} unidad${p.stock !== 1 ? 'es' : ''} restante${p.stock !== 1 ? 's' : ''}.`,
      relatedId: p.id
    });
    sendDesktopNotification({
      title: isOut ? ' Producto Agotado — Savidna' : 'Stock Bajo — Savidna',
      body: isOut ? `${p.name} está agotado. Reabastece pronto.` : `${p.name}: quedan ${p.stock} unidad${p.stock !== 1 ? 'es' : ''}.`,
      tag: `stock-${p.id}`,
      requireInteraction: p.stock === 0
    });
  } else {
    // Grouped notification
    let msg = '';
    if (outOfStock.length > 0) msg += `Agotados: ${outOfStock.map(p => p.name).join(', ')}. `;
    if (lowOnly.length > 0) msg += `Stock bajo: ${lowOnly.map(p => `${p.name} (${p.stock})`).join(', ')}.`;
    addNotification({
      type: 'low_stock',
      title: `📦 ${toAlert.length} Productos necesitan reabastecimiento`,
      message: msg.trim()
    });
    sendDesktopNotification({
      title: `📦 ${toAlert.length} Productos con stock bajo — Savidna`,
      body: msg.trim().slice(0, 200),
      tag: 'stock-batch',
      requireInteraction: outOfStock.length > 0
    });
  }
}

/** Limpia el registro de un producto (cuando se repone stock) */
export function clearStockAlert(productId: string) {
  const records = loadRecords();
  delete records[productId];
  saveRecords(records);
}

/** Limpia todos los registros (útil al cerrar sesión) */
export function clearAllStockAlerts() {
  sessionStorage.removeItem(SESSION_KEY);
}